import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Shield, CheckCircle, AlertTriangle, XCircle, FileText, Loader2 } from 'lucide-react';

const BASE = process.env.REACT_APP_BACKEND_URL;

const fmt = (v) => {
  const n = Number(v || 0);
  return `€ ${n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const OUTCOMES = [
  {
    key: 'approved',
    label: 'APPROVATO SENZA RISERVE',
    description: 'Approvo integralmente il SAL e autorizzo la fatturazione entro l’importo indicato.',
    color: 'emerald',
  },
  {
    key: 'approved_with_reservations',
    label: 'APPROVATO CON RISERVE',
    description: 'Approvo il SAL con le riserve indicate; motivare nel campo dedicato.',
    color: 'amber',
  },
  {
    key: 'rejected',
    label: 'NON APPROVATO',
    description: 'Non approvo il SAL; indicare la motivazione nel campo dedicato.',
    color: 'rose',
  },
];

export default function PublicSalApprovalPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [approval, setApproval] = useState(null);
  const [error, setError] = useState(null);
  const [readConfirmed, setReadConfirmed] = useState(false);
  const [outcome, setOutcome] = useState('');
  const [authorizedAmount, setAuthorizedAmount] = useState('');
  const [reservations, setReservations] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [otpRequested, setOtpRequested] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otp, setOtp] = useState('');
  const [signing, setSigning] = useState(false);
  const [signedResult, setSignedResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BASE}/api/public/sal-approvals/${token}`);
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j?.detail?.code || j?.detail?.message || `HTTP ${res.status}`);
        }
        const data = await res.json();
        setApproval(data);
        setAuthorizedAmount(String(data?.amount_authorizable_max || 0));
      } catch (e) {
        setError(String(e.message || e));
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleRequestOtp = async () => {
    if (!readConfirmed) return toast.error('Confermare di aver letto integralmente il documento');
    if (!outcome) return toast.error('Selezionare un esito');
    setOtpVerifying(false);
    try {
      const res = await fetch(`${BASE}/api/public/sal-approvals/${token}/otp/request`, { method: 'POST' });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.detail?.code || `HTTP ${res.status}`);
      }
      setOtpRequested(true);
      toast.success("OTP inviato all'indirizzo email registrato");
    } catch (e) {
      toast.error(`Invio OTP fallito: ${e.message}`);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length < 6) return toast.error('OTP di 6 cifre');
    setOtpVerifying(true);
    try {
      const res = await fetch(`${BASE}/api/public/sal-approvals/${token}/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ otp }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.detail?.code || `HTTP ${res.status}`);
      }
      setOtpVerified(true);
      toast.success('OTP verificato');
    } catch (e) {
      toast.error(`Verifica OTP fallita: ${e.message}`);
    } finally {
      setOtpVerifying(false);
    }
  };

  const validateBeforeSign = () => {
    if (!readConfirmed) return 'Confermare la lettura integrale del documento';
    if (!outcome) return 'Selezionare un esito';
    const amt = Number(authorizedAmount);
    const max = Number(approval?.amount_authorizable_max || 0);
    if (outcome === 'approved') {
      if (isNaN(amt) || amt <= 0 || amt > max + 0.01) return `Importo autorizzato non valido (max ${fmt(max)})`;
      if (reservations && reservations.trim()) return "APPROVATO SENZA RISERVE non può contenere riserve";
    }
    if (outcome === 'approved_with_reservations') {
      if (!reservations || !reservations.trim()) return 'Riserve obbligatorie';
      if (isNaN(amt) || amt <= 0 || amt > max + 0.01) return `Importo autorizzato non valido (max ${fmt(max)})`;
    }
    if (outcome === 'rejected') {
      if (!rejectionReason || !rejectionReason.trim()) return 'Motivazione mancata approvazione obbligatoria';
    }
    return null;
  };

  const handleSign = async () => {
    const err = validateBeforeSign();
    if (err) return toast.error(err);
    if (!otpVerified) return toast.error('Verificare prima il codice OTP');
    setSigning(true);
    try {
      const idKey = `sign-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const payload = {
        otp,
        outcome,
        authorized_amount: outcome === 'rejected' ? 0 : Number(authorizedAmount),
        reservations: outcome === 'approved_with_reservations' ? reservations : null,
        rejection_reason: outcome === 'rejected' ? rejectionReason : null,
        read_confirmation: readConfirmed,
        idempotency_key: idKey,
      };
      const res = await fetch(`${BASE}/api/public/sal-approvals/${token}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.detail?.code || j?.detail || `HTTP ${res.status}`);
      }
      const j = await res.json();
      setSignedResult(j);
    } catch (e) {
      toast.error(`Sottoscrizione fallita: ${e.message}`);
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-500" />
          <p className="text-slate-600 mt-3">Caricamento SAL…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Card className="max-w-lg" data-testid="public-sal-error">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-rose-700">
              <XCircle className="w-6 h-6" /> Link non valido
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-slate-700">
              Il link non è valido, è scaduto o è stato revocato. Per una nuova firma
              contattare il Project Manager di riferimento.
            </p>
            <p className="text-xs text-slate-500 font-mono">Codice: {error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (signedResult) {
    const labels = {
      approved: 'APPROVATO SENZA RISERVE',
      approved_with_reservations: 'APPROVATO CON RISERVE',
      rejected: 'NON APPROVATO',
    };
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Card className="max-w-2xl w-full" data-testid="public-sal-signed">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-emerald-700">
              <CheckCircle className="w-7 h-7" /> Sottoscrizione registrata
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-slate-700">
              La sottoscrizione elettronica del SAL <strong>{approval.sal_number}</strong> è stata
              acquisita correttamente.
            </p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-slate-500">Esito:</span> <strong>{labels[signedResult.outcome]}</strong></div>
              <div><span className="text-slate-500">Firma ID:</span> <span className="font-mono text-xs">{signedResult.signature_id}</span></div>
              <div><span className="text-slate-500">Data e ora:</span> {new Date(signedResult.signed_at).toLocaleString('it-IT')}</div>
              <div className="col-span-2"><span className="text-slate-500">Hash signed.pdf:</span> <span className="font-mono text-[10px] break-all">{signedResult.signed_pdf_sha256}</span></div>
            </div>
            <p className="text-xs text-slate-500 pt-4 border-t">
              Sottoscrizione elettronica con identificazione tramite OTP e audit trail.
              Una copia del documento firmato è conservata dal sistema.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isSuperseded = approval?.status === 'superseded';
  const alreadySigned = ['approved', 'approved_with_reservations', 'rejected', 'invoiced'].includes(approval?.status);
  const econ = approval?.economic || {};

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4" data-testid="public-sal-page">
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-slate-700" />
            <h1 className="text-xl font-semibold text-slate-900">Approvazione SAL — Cliente</h1>
          </div>
          <span className="text-xs text-slate-500 font-mono">v{approval?.version || 1}</span>
        </div>

        {isSuperseded && (
          <div className="bg-slate-800 text-white p-4 rounded-lg flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Questa versione è stata sostituita da un'emissione più recente. Non è sottoscrivibile.
          </div>
        )}
        {alreadySigned && !isSuperseded && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-4 rounded-lg flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            Questa approvazione risulta già completata (stato: {approval.status}). Non è possibile
            modificarla.
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Dati SAL</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <div><div className="text-slate-500">Numero SAL</div><div className="font-semibold" data-testid="public-sal-number">{approval.sal_number}</div></div>
            <div><div className="text-slate-500">Progetto</div><div className="font-semibold">{approval.project_number}</div></div>
            <div><div className="text-slate-500">Committente</div><div className="font-semibold">{approval.client_name}</div></div>
            <div><div className="text-slate-500">Direttore Lavori</div><div className="font-semibold">{approval.director?.name || '—'}{approval.director?.qualification ? ` · ${approval.director.qualification}` : ''}</div></div>
            <div><div className="text-slate-500">Avanzamento</div><div className="font-semibold">{Number(econ.sal_percentage || 0).toFixed(1)}%</div></div>
            <div><div className="text-slate-500">Importo SAL corrente</div><div className="font-semibold">{fmt(econ.amount_this_sal)}</div></div>
            <div className="md:col-span-3 border-t pt-3 mt-1">
              <div className="text-slate-500 text-xs">Importo massimo autorizzabile</div>
              <div className="text-2xl font-bold text-slate-900" data-testid="public-sal-max-authorizable">{fmt(econ.amount_authorizable_max)}</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><FileText className="w-5 h-5" /> Documento PDF</CardTitle>
          </CardHeader>
          <CardContent>
            <iframe
              src={`${BASE}/api/public/sal-approvals/${token}/pdf`}
              title="Report SAL"
              className="w-full h-[70vh] border rounded"
              data-testid="public-sal-pdf-iframe"
            />
            <a
              href={`${BASE}/api/public/sal-approvals/${token}/pdf`}
              target="_blank" rel="noreferrer"
              className="text-sm underline text-blue-700 mt-2 inline-block"
              data-testid="public-sal-pdf-link"
            >
              Apri il PDF in una nuova scheda
            </a>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Dichiarazione</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="bg-slate-50 border border-slate-200 p-4 rounded text-sm whitespace-pre-wrap text-slate-800">
              {approval.approval_text_snapshot}
            </div>
            <div className="flex items-start gap-2">
              <Checkbox
                id="read-confirm"
                checked={readConfirmed}
                onCheckedChange={(v) => setReadConfirmed(!!v)}
                disabled={alreadySigned || isSuperseded}
                data-testid="public-sal-read-checkbox"
              />
              <Label htmlFor="read-confirm" className="text-sm leading-relaxed">
                <strong>Dichiaro di aver letto integralmente il documento</strong> e di aver
                verificato le lavorazioni contabilizzate prima di procedere.
              </Label>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Esito</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {OUTCOMES.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => setOutcome(o.key)}
                  disabled={alreadySigned || isSuperseded}
                  data-testid={`public-sal-outcome-${o.key}`}
                  className={`text-left p-4 rounded-lg border-2 transition ${
                    outcome === o.key
                      ? `border-${o.color}-500 bg-${o.color}-50`
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <div className={`font-semibold text-sm text-${o.color}-700`}>{o.label}</div>
                  <div className="text-xs text-slate-600 mt-1">{o.description}</div>
                </button>
              ))}
            </div>

            {outcome === 'approved' && (
              <div className="space-y-2">
                <Label>Importo autorizzato</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={authorizedAmount}
                  onChange={(e) => setAuthorizedAmount(e.target.value)}
                  data-testid="public-sal-authorized-amount"
                />
                <p className="text-xs text-slate-500">Massimo: {fmt(econ.amount_authorizable_max)}</p>
              </div>
            )}
            {outcome === 'approved_with_reservations' && (
              <div className="space-y-3">
                <div>
                  <Label>Riserve / Osservazioni *</Label>
                  <Textarea
                    value={reservations}
                    onChange={(e) => setReservations(e.target.value)}
                    rows={4}
                    placeholder="Descrivere le riserve..."
                    data-testid="public-sal-reservations"
                  />
                </div>
                <div>
                  <Label>Importo autorizzato</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={authorizedAmount}
                    onChange={(e) => setAuthorizedAmount(e.target.value)}
                    data-testid="public-sal-authorized-amount-reserve"
                  />
                  <p className="text-xs text-slate-500 mt-1">Massimo: {fmt(econ.amount_authorizable_max)}</p>
                </div>
              </div>
            )}
            {outcome === 'rejected' && (
              <div>
                <Label>Motivazione mancata approvazione *</Label>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={4}
                  placeholder="Descrivere la motivazione..."
                  data-testid="public-sal-rejection-reason"
                />
              </div>
            )}
          </CardContent>
        </Card>

        {!alreadySigned && !isSuperseded && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Sottoscrizione con codice OTP</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {!otpRequested ? (
                <Button
                  onClick={handleRequestOtp}
                  className="w-full md:w-auto"
                  disabled={!readConfirmed || !outcome}
                  data-testid="public-sal-request-otp"
                >
                  Invia codice OTP alla mia email
                </Button>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-slate-600">
                    Codice OTP inviato a <strong>{approval.signer_email}</strong>. Il codice
                    scade in 10 minuti.
                  </p>
                  <div className="flex gap-2 items-center">
                    <Input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      placeholder="Codice a 6 cifre"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                      className="max-w-[200px] text-center tracking-widest text-xl"
                      disabled={otpVerified}
                      data-testid="public-sal-otp-input"
                    />
                    {!otpVerified ? (
                      <Button onClick={handleVerifyOtp} disabled={otpVerifying || otp.length < 6}
                              data-testid="public-sal-verify-otp">
                        {otpVerifying ? 'Verifico…' : 'Verifica'}
                      </Button>
                    ) : (
                      <span className="text-emerald-700 text-sm flex items-center gap-1">
                        <CheckCircle className="w-5 h-5" /> Verificato
                      </span>
                    )}
                    <Button variant="outline" size="sm" onClick={handleRequestOtp}
                            disabled={otpVerified}>
                      Reinvia
                    </Button>
                  </div>
                </div>
              )}

              <div className="pt-3 border-t">
                <Button
                  onClick={handleSign}
                  disabled={!otpVerified || signing}
                  className="w-full md:w-auto"
                  data-testid="public-sal-sign-button"
                >
                  {signing ? 'Registrazione…' :
                    outcome === 'rejected' ? 'Registra mancata approvazione'
                    : 'Approva e sottoscrivi SAL'}
                </Button>
                <p className="text-xs text-slate-500 mt-2">
                  Sottoscrizione elettronica con identificazione tramite OTP e audit trail.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="text-center text-xs text-slate-400 pt-4">
          Sistema Abitalo CRM · Documento: SAL {approval.sal_number} v{approval.version} ·
          Hash issued.pdf: <span className="font-mono">{(approval.issued_pdf_sha256 || '').slice(0, 16)}…</span>
        </div>
      </div>
    </div>
  );
}
